// Shell wiring. The HTML (header, hero, search, drawer, disclaimer, poster) is static and paints
// before this runs; MapLibre loads afterwards through a dynamic import (design D1, G7).

import "./style.css";
import { loadAnalytics } from "./analytics";
import { HERO_LAYERS, HERO_VIEW, PITCH_3D, type View } from "./config";
import { attachDisclaimer } from "./disclaimer";
import { formatHash, honouredLayers, parseHash } from "./hash";
import { renderDrawer } from "./legend";
import { fetchManifest, selectStyled, type StyledLayer } from "./manifest";
import type { AtlasMap } from "./map";
import { describeFeatures, renderPanel } from "./panel";
import { attachSearch, type Place } from "./search";
import type { Theme } from "./style";
import { STYLES } from "./styles";

const $ = <T extends HTMLElement>(sel: string) => document.querySelector<T>(sel)!;
const root = document.documentElement;
const params = new URLSearchParams(location.search);
// ?poster=dark|light: the hero view with no chrome, for scripts/poster.ts.
const posterMode = params.get("poster");

function storedTheme(): Theme {
  if (posterMode === "light" || posterMode === "dark") return posterMode;
  try {
    return localStorage.getItem("atlas-theme") === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

const deepLink = parseHash(location.hash);
let theme: Theme = storedTheme();
let layers: StyledLayer[] = [];
let visible = new Set<string>();
let basemapUrl: string | undefined;
let atlas: AtlasMap | undefined;
let pendingPlace: Place | undefined;
let explored = !!deepLink; // hero untouched: don't write the hash, so a reload keeps the hero
let view: View = deepLink?.view ?? HERO_VIEW;

const state = () => ({ theme, basemapUrl, layers, visible });

function writeHash() {
  if (!explored || posterMode) return;
  history.replaceState(null, "", formatHash({ view, layers: [...visible] }));
}

function explore() {
  if (explored) return;
  explored = true;
  root.classList.add("explored");
}

function redraw() {
  renderDrawer($("#layer-list"), layers, visible, (id, on) => {
    explore();
    if (on) visible.add(id);
    else visible.delete(id);
    redraw();
    // The drawer is rebuilt; put keyboard focus back on the checkbox that was just toggled.
    document.getElementById(`toggle-${id}`)?.focus();
    atlas?.setState(state());
    writeHash();
  });
}

function applyTheme(next: Theme) {
  theme = next;
  root.dataset.theme = next;
  $("#theme-toggle").setAttribute("aria-pressed", String(next === "light"));
  $("#theme-toggle").textContent = next === "light" ? "Dark map" : "Light map";
  try {
    localStorage.setItem("atlas-theme", next);
  } catch {
    // private mode: the choice just isn't remembered
  }
  atlas?.setState(state());
}

function wireShell() {
  attachDisclaimer();
  root.dataset.theme = theme;
  // index.html guesses a deep link from the comma count alone; undo that for a hash parseHash rejects.
  if (deepLink) root.classList.add("deeplink", "explored");
  else root.classList.remove("deeplink", "explored");
  if (posterMode) root.classList.add("poster-mode");

  $("#theme-toggle").addEventListener("click", () => applyTheme(theme === "dark" ? "light" : "dark"));
  const pitch = $("#pitch-toggle");
  pitch.setAttribute("aria-pressed", String(view.pitch > 0));
  pitch.addEventListener("click", () => {
    explore();
    const tilt = !(atlas ? atlas.view().pitch > 0 : view.pitch > 0);
    pitch.setAttribute("aria-pressed", String(tilt));
    // Before the map exists, the choice goes into the view it will be created with.
    if (atlas) atlas.setPitch(tilt ? PITCH_3D : 0);
    else view = { ...view, pitch: tilt ? PITCH_3D : 0 };
  });

  const drawer = $("#drawer");
  const drawerButton = $("#drawer-toggle");
  const setDrawer = (open: boolean) => {
    drawer.dataset.open = String(open);
    drawerButton.setAttribute("aria-expanded", String(open));
  };
  setDrawer(window.matchMedia("(min-width: 900px)").matches);
  drawerButton.addEventListener("click", () => setDrawer(drawer.dataset.open !== "true"));
  $("#drawer-close").addEventListener("click", () => setDrawer(false));

  attachSearch($<HTMLFormElement>("#search"), {
    onSelect(place) {
      explore();
      if (atlas) atlas.flyTo(place.lng, place.lat);
      else pendingPlace = place;
    },
  });
}

async function start() {
  wireShell();
  try {
    const manifest = await fetchManifest();
    basemapUrl = manifest.basemap?.url ? new URL(manifest.basemap.url, location.href).href : undefined;
    layers = selectStyled(manifest, STYLES);
  } catch (e) {
    console.error(e);
    $("#map-status").textContent = "Map data is unavailable right now.";
    $("#map-status").hidden = false;
  }
  const ids = layers.map((l) => l.entry.id);
  visible = new Set(honouredLayers(deepLink ? deepLink.layers : HERO_LAYERS, ids));
  redraw();

  const { createMap } = await import("./map");
  const panel = $("#panel");
  atlas = createMap($("#map"), state(), view, {
    onClick(features) {
      explore();
      renderPanel(panel, describeFeatures(features, layers), () => (panel.hidden = true));
    },
    onUserMove: explore,
    onMoveEnd(v) {
      view = v;
      $("#pitch-toggle").setAttribute("aria-pressed", String(v.pitch > 0));
      writeHash();
    },
    onFirstIdle() {
      root.classList.add("map-ready");
      if (posterMode) (window as unknown as { __posterReady?: boolean }).__posterReady = true;
      else loadAnalytics();
    },
  });
  if (pendingPlace) atlas.flyTo(pendingPlace.lng, pendingPlace.lat);
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!deepLink && !posterMode && !reducedMotion) atlas.drift();
}

void start();
